import React from 'react'
import { Card, CardContent, Box, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { RichText as RichTextType } from '../../types/post'
import Image from '../ContentfulEntries/Image'
import { BLOCKS } from '@contentful/rich-text-types'
import Link from '../Link'
import useUrlPathBuilder from '../../hooks/useUrlPathBuilder'
import { AssetFluid } from '../../types/asset'
import RichText from '../RichText'
import useLocation from 'react-use/lib/useLocation'

interface ArticleComponentProps {
  slug: string
  featuredImage: AssetFluid | null
  title: string
  excerpt: RichTextType
}

export const Article: React.FC<ArticleComponentProps> = ({
  slug,
  featuredImage,
  title,
  excerpt,
}) => {
  const { classes, cx } = useStyles()

  const { pathname } = useLocation()

  let successStoryArticlePrefix: string | undefined

  if (pathname?.includes('success-stories')) {
    // Use the same URL prefix for similar success stories
    // /new/success-stories/mike => new/success-stories
    successStoryArticlePrefix = pathname.substring(0, pathname.lastIndexOf('/'))
  }

  const { blogPrefix, urlPathBuilder } = useUrlPathBuilder()

  return (
    <Box className={cx(classes.container)}>
      <Card
        className={classes.content}
        elevation={0}
        component={(props) => (
          // @ts-ignore
          <Link
            to={urlPathBuilder([successStoryArticlePrefix ?? blogPrefix, slug])}
            {...props}
          />
        )}
      >
        {featuredImage && (
          <Box className={classes.imageWrapper}>
            <Image
              asset={featuredImage}
              className={cx(classes.column, classes.img)}
            />
          </Box>
        )}
        <CardContent className={classes.detail}>
          <Typography className={classes.title} variant="h3">
            {title}
          </Typography>
          <Box>
            <RichText
              rawBody={excerpt.raw}
              references={excerpt.references}
              options={{
                props: {
                  [BLOCKS.PARAGRAPH]: {
                    className: classes.text,
                  },
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
      justifyContent: 'space-around',
    },
    alignItems: 'center',
    margin: '0',
    paddingBottom: 0,
    maxWidth: '30%',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      maxWidth: 'unset',
      width: '100%',

      '&:last-child': {
        display: 'none',
      },

      '&:nth-last-child(2)': {
        '& > a > div:last-child': {
          paddingBottom: 0,
        },
      },
    },
  },

  content: {
    flexGrow: 1,
    flexBasis: '30%',
    marginRight: '0em',
    padding: 0,

    '&:hover': {
      textDecoration: 'none',
    },
  },

  detail: {
    '&&': {
      padding: '1em 0 3em',

      '@media (max-width: 375px)': {
        padding: '1em 0 2.5em',
      },
    },
  },
  title: {
    color: theme.palette.text.primary,
    fontWeight: 'bold',
  },
  column: {
    height: '100%',
    width: '100%',
  },
  text: {
    color: theme.palette.text.primary,
    marginTop: '0.5rem',
  },
  imageWrapper: {
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  img: {
    '&&': {
      height: 'auto',
      aspectRatio: '5/3',
      maxHeight: 'none !important',
      maxWidth: 'none !important',
      transition: 'transform .2s',

      '&:hover': {
        transform: 'scale(1.1)',
      },
    },
  },
}))
